<template>
  <div v-if="isProcessing">
    <div class="d-flex align-center justify-lg-space-between pa-5">
      <div class="mr-4">
        <p class="mb-0 text-body-1">
          {{ $tc('component.bookmarks.import.in_progress') }}
          <span
            v-if="activeImportCount > 1"
            class="text-caption text-neutral400 pl-1"
          >({{ activeImportCount }})</span>
          <span
            v-if="displayPercentage > 0 && showPercentage"
            class="font-weight-semibold pl-2"
          >
            {{ displayPercentage }}%
          </span>
        </p>
        <p v-if="showProgressCounter && bookmarksProcessed !== undefined && totalBookmarks" class="mb-0 text-caption text-neutral400">
          {{ bookmarksProcessed }} / {{ totalBookmarks }} {{ $t('component.bookmarks.import.items', totalBookmarks) }}
        </p>
      </div>
    </div>
    <v-progress-linear
      :value="displayPercentage"
      color="blue400"
      height="4"
      :indeterminate="displayPercentage === 0"
    />
  </div>

  <div v-else-if="(status === 'failed' || status === 'timeout') && !suppressErrorMessages">
    <div class="d-flex align-center justify-lg-space-between pa-5">
      <div class="mr-4">
        <p class="mb-0 text-body-1 text-error">
          {{ status === 'timeout' ? 'Import timed out' : $tc('component.bookmarks.import.failed') }}
        </p>
        <p v-if="status === 'timeout' && error" class="mb-0 text-caption text-neutral400">
          {{ error }}
        </p>
        <p v-else-if="bookmarksFailed" class="mb-0 text-caption text-neutral400">
          {{ bookmarksFailed }} {{ $t('component.bookmarks.import.items_failed', bookmarksFailed) }}
        </p>
      </div>
      <BrandButton color="blue25" size="small" elevation="0" variant="flat" icon @click="closeToast">
        <PhX class="text-h5 text-neutral400" />
      </BrandButton>
    </div>
  </div>

  <div v-else>
    <div class="d-flex align-center justify-lg-space-between pa-5">
      <div class="mr-4">
        <p class="mb-0 text-body-1">
          <span>{{ $tc('component.bookmarks.import.done') }}</span>
          <button
            class="ml-2 text-teal600 cursor-pointer text-decoration-underline mb-0 font-weight-medium text-body-1"
            @click="handleRefreshClick"
          >
            {{ $tc('component.bookmarks.import.load_items') }}
          </button>
        </p>
        <p v-if="bookmarksProcessed !== undefined && totalBookmarks" class="mb-0 text-caption text-neutral400">
          {{ bookmarksProcessed }} {{ $t('component.bookmarks.import.items_imported', bookmarksProcessed) }}
        </p>
      </div>
      <BrandButton color="blue25" size="small" elevation="0" variant="flat" icon @click="closeToast">
        <PhX class="text-h5 text-neutral400" />
      </BrandButton>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTranslation } from '@/composables/useTranslation'
import { PhX } from '@phosphor-icons/vue'
import BrandButton from '@/components/global/brand/BrandButton.vue'
import eventbus from '@/plugins/eventbus'

// CONFIG is defined globally in vite.config.js
/* global CONFIG */

// Define props with the same structure as the original component
const props = defineProps({
  showPercentage: {
    type: Boolean,
    default: true,
  },
  isRunning: {
    type: Boolean,
    default: false,
  },
  timeout: {
    type: Number,
    default: 10000,
  },
  lastImportSize: {
    type: [Number],
    default: 0,
  },
  importQueueSize: {
    type: [Number],
    default: 0,
  },
  // New props for WebSocket real-time updates
  progressPercentage: {
    type: Number,
    default: null,
  },
  status: {
    type: String,
    default: 'processing',
    validator: (value) => ['pending', 'processing', 'completed', 'failed', 'timeout'].includes(value),
  },
  totalBookmarks: {
    type: Number,
    default: null,
  },
  bookmarksProcessed: {
    type: Number,
    default: null,
  },
  bookmarksFailed: {
    type: Number,
    default: 0,
  },
  error: {
    type: String,
    default: null,
  },
  suppressErrorMessages: {
    type: Boolean,
    default: true, // Set to true to always show "Import done" message instead of error/timeout messages
  },
  showProgressCounter: {
    type: Boolean,
    default: true, // Show the item counter during import progress
  },
  activeImportCount: {
    type: Number,
    default: 1,
  },
})

// Define emits
const emit = defineEmits(['close'])

// Setup i18n
const { t, tc } = useTranslation()

// Sticky latch: once the import reaches "done", never flip back to progress view.
// This prevents flicker from late-arriving WebSocket/polling events that reset progressPercentage.
const hasReachedDone = ref(false)

watch(
  () => [props.progressPercentage, props.status],
  ([percentage, status]) => {
    if (percentage >= 100 || ['completed', 'failed', 'timeout'].includes(status)) {
      hasReachedDone.value = true
    }
  },
  { immediate: true }
)

// Computed properties
const isProcessing = computed(() => {
  if (hasReachedDone.value) return false
  return props.status === 'processing' || props.status === 'pending' ||
         (props.isRunning && props.importQueueSize > 1)
})

const displayPercentage = computed(() => {
  // Use WebSocket progress percentage if available
  if (props.progressPercentage !== null && props.progressPercentage !== undefined) {
    const wsPercentage = Math.floor(props.progressPercentage)
    console.log('[BookmarkImport] Using WebSocket percentage:', wsPercentage)
    return wsPercentage
  }

  // Fallback to calculated percentage from queue sizes
  if (props.lastImportSize === 0) {
    console.log('[BookmarkImport] lastImportSize is 0, returning 0%')
    return 0
  }

  const percentage = ((props.lastImportSize - props.importQueueSize) / props.lastImportSize) * 100
  const calculated = percentage < 1 && percentage > 0 ? 1 : Math.floor(percentage)

  console.log('[BookmarkImport] Calculated percentage:', {
    lastImportSize: props.lastImportSize,
    importQueueSize: props.importQueueSize,
    calculated
  })

  return calculated
})

// For backward compatibility
const importPercentage = computed(() => displayPercentage.value)

// Debug watchers
watch(() => props.progressPercentage, (newVal, oldVal) => {
  console.log('[BookmarkImport] progressPercentage changed:', oldVal, '->', newVal)
})

watch(() => props.status, (newVal, oldVal) => {
  console.log('[BookmarkImport] status changed:', oldVal, '->', newVal)
})

watch(() => props.bookmarksProcessed, (newVal, oldVal) => {
  console.log('[BookmarkImport] bookmarksProcessed changed:', oldVal, '->', newVal)
})

watch(() => props.totalBookmarks, (newVal, oldVal) => {
  console.log('[BookmarkImport] totalBookmarks changed:', oldVal, '->', newVal)
})

watch(isProcessing, (newVal) => {
  console.log('[BookmarkImport] isProcessing changed to:', newVal)
})

watch(displayPercentage, (newVal) => {
  console.log('[BookmarkImport] displayPercentage changed to:', newVal)
})

// Methods
const closeToast = () => {
  emit('close')
}

const handleRefreshClick = () => {
  eventbus.emit('collectionItems:manual-refresh')
  closeToast()
}
</script>
